import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import { CoachSlot } from './CoachSlot';
import type { AiUsage, CoachAnalysis as CoachAnalysisData } from '@/types/qbot';

interface CoachOverlayProps {
  open: boolean;
  onClose: () => void;
  isPro: boolean;
  loading: boolean;
  error: unknown;
  analysis?: CoachAnalysisData;
  usage?: AiUsage;
}

/**
 * The AI coach answer, over the deck — not under it.
 *
 * The deck stage is a fixed, non-scrolling frame, so the coach's variable-length
 * answer can't live inline without pushing the layout around. It lands here as a
 * scrollable overlay painted over the whole stage instead, closed with the ×.
 */
export function CoachOverlay({
  open,
  onClose,
  isPro,
  loading,
  error,
  analysis,
  usage,
}: CoachOverlayProps) {
  const { t } = useTranslation('cr');
  if (!open) return null;

  return (
    <div className="absolute inset-0 z-20 flex flex-col rounded-xl bg-bg/95 backdrop-blur">
      <div className="flex shrink-0 items-center justify-between px-1 pb-1.5 pt-0.5">
        <span className="text-[11px] font-bold uppercase tracking-wide text-fg-muted">
          {t('coach.title')}
        </span>
        <button
          type="button"
          aria-label={t('coach.close', { defaultValue: 'Close' })}
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded-md bg-bg-secondary text-fg active:opacity-80"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-0.5">
        {/* CoachSlot already renders the paywall / thinking / answer states. */}
        <CoachSlot
          isPro={isPro}
          open
          loading={loading}
          error={error}
          analysis={analysis}
          usage={usage}
        />
      </div>
    </div>
  );
}
